import styled from '@emotion/styled';
import { useFormValues } from '@eventbrite/event-create-ui';
import { gettext } from '@eventbrite/i18n';
import {
    CalendarIcon,
    MapPinIcon,
    ToggleGroup,
    ToggleGroupItem,
    Typography,
    VideoIcon,
} from '@eventbrite/marmalade';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { LocationType } from '../../../../shared';
import { AutoCreateEventFormValues } from '../../types';
import { VenueLocationType } from './VenueLocationType';

export enum VenueEditTestIds {
    VenueContentPicker = 'VenueContentPicker',
    SMSVerificationContent = 'SMSVerificationContent',
}

const CONTENT_VENUE_BY_TYPE = {
    [LocationType.Venue]: <VenueLocationType />,
    [LocationType.Online]: (
        <Typography variant="body-md" color="ui-700">
            {gettext(
                'Online events have unique event pages where you can add links to livestreams and more',
            ).toString()}
        </Typography>
    ),
    [LocationType.ToBeAnnounced]: null,
};
export const VenueLocation = () => {
    const { control } = useFormContext();
    const { locationType } = useFormValues<AutoCreateEventFormValues>();
    const contentByType = CONTENT_VENUE_BY_TYPE[locationType] || null;

    return (
        <>
            <Typography as="h2" color="neutral-900" variant="body-lg">
                {gettext('Where is it located?')}
            </Typography>

            <VenueContentPicker
                data-testid={VenueEditTestIds.VenueContentPicker}
            >
                <Controller
                    name="locationType"
                    control={control}
                    defaultValue={LocationType.Venue}
                    render={({ field }) => {
                        return (
                            <ToggleGroup
                                aria-label={gettext(
                                    'Event location type',
                                ).toString()}
                                defaultValue={field.value}
                                onValueChange={field.onChange}
                                isDeselectable={false}
                            >
                                <ToggleGroupItem
                                    aria-label={gettext(
                                        'Venue location type',
                                    ).toString()}
                                    value={LocationType.Venue}
                                >
                                    <MapPinIcon />
                                    {gettext('Venue')}
                                </ToggleGroupItem>
                                <ToggleGroupItem
                                    aria-label={gettext(
                                        'Online location type',
                                    ).toString()}
                                    value={LocationType.Online}
                                >
                                    <VideoIcon />
                                    {gettext('Online event')}
                                </ToggleGroupItem>

                                <ToggleGroupItem
                                    value={LocationType.ToBeAnnounced}
                                    aria-label={gettext(
                                        'To be announced location type',
                                    ).toString()}
                                >
                                    <CalendarIcon />
                                    {gettext('To be announced')}
                                </ToggleGroupItem>
                            </ToggleGroup>
                        );
                    }}
                />
            </VenueContentPicker>

            <Content>{contentByType}</Content>
        </>
    );
};

const VenueContentPicker = styled.div`
    display: inline-flex;
    max-width: 100%;
    overflow: auto;
    margin: 1.5rem 0 1rem;

    button {
        white-space: nowrap;
    }
`;

const Content = styled.div`
    margin-top: 0.5rem;
`;
